/**
 * Formatação pt-BR para a página de status.
 *
 * Todas as funções toleram `null`, `undefined` e datas inválidas: o snapshot vem da
 * rede e um campo torto não pode derrubar a tela justamente durante um incidente.
 *
 * O fuso é fixado em America/Sao_Paulo de propósito. A base de clientes é brasileira
 * e um horário de incidente precisa ser o mesmo para quem lê e para quem atende.
 */

const FUSO = 'America/Sao_Paulo';

function paraData(valor: string | null | undefined): Date | null {
  if (!valor) {
    return null;
  }

  const data = new Date(valor);

  return Number.isNaN(data.getTime()) ? null : data;
}

const formatadorDataHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const formatadorHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO,
  hour: '2-digit',
  minute: '2-digit',
});

const formatadorDiaMes = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
});

const formatadorDataExtensa = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO,
  day: 'numeric',
  month: 'long',
});

const formatadorDataCompleta = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatarDataHoraBR(valor: string | null | undefined): string {
  const data = paraData(valor);

  return data ? formatadorDataHora.format(data) : '—';
}

export function formatarHoraBR(valor: string | null | undefined): string {
  const data = paraData(valor);

  return data ? formatadorHora.format(data) : '—';
}

export function formatarDiaMes(valor: string | null | undefined): string {
  const data = paraData(valor);

  return data ? formatadorDiaMes.format(data) : '—';
}

export function formatarDataExtensa(valor: string | null | undefined): string {
  const data = paraData(valor);

  return data ? formatadorDataExtensa.format(data) : '—';
}

export function formatarDataCompleta(valor: string | null | undefined): string {
  const data = paraData(valor);

  return data ? formatadorDataCompleta.format(data) : '—';
}

/** Janela de manutenção no formato "01:00 — 03:00". */
export function formatarJanela(
  inicio: string | null | undefined,
  fim: string | null | undefined,
): string {
  const de = formatarHoraBR(inicio);
  const ate = formatarHoraBR(fim);

  if (de === '—' && ate === '—') {
    return '—';
  }

  return `${de} — ${ate}`;
}

/** Percentual com duas casas e vírgula decimal: 99,98%. */
export function formatarPercentual(valor: number | null | undefined): string {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    return '—';
  }

  return `${valor.toFixed(2).replace('.', ',')}%`;
}

/** Duração legível a partir de minutos: "9 min", "1 h 12 min". */
export function formatarDuracaoMinutos(minutos: number | null | undefined): string {
  if (typeof minutos !== 'number' || !Number.isFinite(minutos) || minutos < 0) {
    return '—';
  }

  const arredondado = Math.round(minutos);

  if (arredondado < 60) {
    return `${arredondado} min`;
  }

  const horas = Math.floor(arredondado / 60);
  const resto = arredondado % 60;

  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

/**
 * Idade de um dado em linguagem natural: "agora mesmo", "há 3 min", "há 2 h".
 * Usado no rodapé para a última verificação.
 */
export function formatarIdadeRelativa(segundos: number | null | undefined): string {
  if (typeof segundos !== 'number' || !Number.isFinite(segundos) || segundos < 0) {
    return 'há instantes';
  }

  if (segundos < 60) {
    return 'agora mesmo';
  }

  const minutos = Math.floor(segundos / 60);

  if (minutos < 60) {
    return `há ${minutos} min`;
  }

  const horas = Math.floor(minutos / 60);

  if (horas < 24) {
    return `há ${horas} h`;
  }

  const dias = Math.floor(horas / 24);

  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

/**
 * Momento relativo ao dia corrente: "hoje 14:49" ou "18/08 14:44".
 *
 * Durante um incidente quase tudo aconteceu hoje, e repetir a data em cada linha da
 * linha do tempo só rouba atenção do que mudou.
 */
export function formatarMomentoCurto(valor: string | null | undefined, separador = ' '): string {
  const data = paraData(valor);

  if (!data) {
    return '—';
  }

  const hojeSP = formatadorDiaMes.format(new Date());
  const diaDoValor = formatadorDiaMes.format(data);
  const hora = formatadorHora.format(data);

  return diaDoValor === hojeSP ? `hoje${separador}${hora}` : `${diaDoValor}${separador}${hora}`;
}

/** Início de um incidente por extenso: "hoje às 14:41" ou "18/08 às 14:41". */
export function formatarInicioIncidente(valor: string | null | undefined): string {
  return formatarMomentoCurto(valor, ' às ');
}
